import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AxiosResponse } from 'axios';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Table as ReactTable } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
  Column,
  Filters,
  HeaderGroup,
  Row,
  SortingRule,
  TableState,
  useFilters,
  UseFiltersState,
  useFlexLayout,
  usePagination,
  useSortBy,
  UseSortByState,
  useTable,
} from 'react-table';
import Skeleton from 'react-loading-skeleton';
import { Help } from '../form';
import { Loader } from '../loader';
import { Pagination } from '../pagination';
import { createRowsFromPage, DefaultColumnFilter, mapFiltersToRequest, mapSortToRequest } from './helpers';

export type TableFetchDataType = {
  page: number;
  size: number;
  filters: Filters<object>;
  sorters: SortingRule<object>[];
};

export type TableProps = {
  defaultSorters?: UseSortByState<object>['sortBy'];
  defaultFilters?: UseFiltersState<object>['filters'];
  //if set to true the filters will be displayed in the column filter and can be overridden. otherwise it is hidden and non modifiable filter.
  displayDefaultFilters?: boolean;
  columns: Column<any>[];
  mapFetchDataArgs?: (args: any) => any;
  pageSize: number;
  disableFilters?: boolean;
  showRefresh?: boolean;
  showClearFilters?: boolean;
  showAddButton?: boolean;
  onFetchData: (args: any) => Promise<AxiosResponse<any>>;
  onRowDoubleClick?: (row: Row) => void;
  onAddButtonClick?: (args: any) => void;
  afterFetchData?: (args: any) => void;
  forceUpdate?: number;
  forceData?: any[];
  title?: string;
  disablePagination?: boolean;
  help?: string;
};

const MAX_ELEMENTS = 999;

const Cell = ({ value }: any) => {
  return <span>{value}</span>;
};

type CustomTableProps = {
  className?: string;
  columns: Column[];
  data: any[];
  fetchData: ({ page, size, filters, sorters }: TableFetchDataType) => void;
  loading: boolean;
  pageCount: number;
  pageSize: number;
  disablePagination?: boolean;
  defaultSorters?: UseSortByState<object>['sortBy'];
  defaultFilters?: UseFiltersState<object>['filters'];
  displayDefaultFilters?: boolean;
  skipPageReset: boolean;
  disableFilters: boolean;
  totalElements: number;
  showRefresh?: boolean;
  showClearFilters?: boolean;
  showAddButton?: boolean;
  onRowDoubleClick?: (row: Row) => void;
  onAddButtonClick?: (args: any) => void;
  forceUpdate?: number;
  title?: string;
  help?: string;
};

function CustomTable({
  className,
  columns,
  data,
  fetchData,
  loading,
  pageCount: controlledPageCount,
  pageSize: controlledPageSize,
  disablePagination,
  defaultSorters,
  defaultFilters,
  displayDefaultFilters,
  skipPageReset,
  disableFilters,
  totalElements,
  showRefresh,
  showClearFilters,
  showAddButton,
  onRowDoubleClick,
  onAddButtonClick,
  forceUpdate,
  title,
  help,
}: CustomTableProps) {
  const { t } = useTranslation();

  const defaultColumn = useMemo(
    () => ({
      Filter: DefaultColumnFilter,
      Cell,
    }),
    []
  );

  const initialState = useMemo(() => {
    const result: Partial<TableState> = {
      pageIndex: 0,
      pageSize: controlledPageSize,
    };

    if (defaultSorters) {
      result.sortBy = defaultSorters;
    }

    if (defaultFilters && displayDefaultFilters) {
      result.filters = defaultFilters;
    }

    return result;
  }, [controlledPageSize, defaultSorters, defaultFilters, displayDefaultFilters]);

  const additionalPlugins = [];

  if (!disablePagination) {
    additionalPlugins.push(usePagination);
  }

  const tableData = React.useMemo(() => (loading ? Array(controlledPageSize).fill({}) : data), [loading, data, controlledPageSize]);
  const tableColumns = React.useMemo(
    () =>
      loading
        ? columns.map((column) => ({
            ...column,
            Cell: <Skeleton width={Number(column.width) * 100} height={6} />,
          }))
        : columns,
    [loading, columns]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page, // Instead of using 'rows', we'll use page,
    // which has only the rows for the active page
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize, filters, sortBy },
    setAllFilters,
  } = useTable(
    {
      columns: tableColumns,
      data: tableData,
      defaultColumn,
      // https://github.com/tannerlinsley/react-table/blob/master/docs/faq.md#how-do-i-stop-my-table-state-from-automatically-resetting-when-my-data-changes
      autoResetPage: !skipPageReset,
      autoResetSortBy: !skipPageReset,
      autoResetFilters: !skipPageReset,
      initialState,
      // Set all plugins in manual mode (everything depends on data from server side, we can't do anything locally)
      manualPagination: true,
      manualSortBy: true,
      manualFilters: true,
      pageCount: controlledPageCount,
      disableFilters,
    },
    useFilters,
    useSortBy,
    useFlexLayout,
    ...additionalPlugins
  );

  const isFirstRun = useRef(true);

  const fetchDataWithDebounce = useMemo(() => _.debounce(fetchData, 200), [fetchData]);
  const pageSizeOptions = useMemo(() => [10, 20, 30, 40, 50], []);

  const effectiveFilters = useMemo(() => {
    return defaultFilters && !displayDefaultFilters
      ? [...defaultFilters.filter((defaulltFilter) => !filters.some((filter) => filter.id === defaulltFilter.id)), ...filters]
      : filters;
  }, [filters, defaultFilters, displayDefaultFilters]);

  const refresh = useMemo(() => {
    return () =>
      fetchDataWithDebounce({
        page: pageIndex,
        size: disablePagination ? MAX_ELEMENTS : pageSize,
        filters: effectiveFilters,
        sorters: sortBy,
      });
  }, [fetchDataWithDebounce, pageIndex, pageSize, sortBy, effectiveFilters, disablePagination]);

  const clearFilters = useMemo(() => {
    return () => setAllFilters(initialState.filters || []);
  }, [setAllFilters, initialState.filters]);

  useEffect(() => {
    if (isFirstRun.current) {
      fetchData({ page: pageIndex, size: disablePagination ? MAX_ELEMENTS : pageSize, filters: effectiveFilters, sorters: sortBy });
      isFirstRun.current = false;
    } else {
      fetchDataWithDebounce({
        page: pageIndex,
        size: disablePagination ? MAX_ELEMENTS : pageSize,
        filters: effectiveFilters,
        sorters: sortBy,
      });
    }
  }, [fetchData, fetchDataWithDebounce, pageIndex, pageSize, sortBy, effectiveFilters, disablePagination]);

  useEffect(() => {
    if (forceUpdate && forceUpdate > 0) {
      refresh();
    }
  }, [refresh, forceUpdate]);

  return (
    <div className={`custom-table${className && className.length > 0 ? ` ${className}` : ''}`}>
      <div className={`tools d-flex mb-2${title ? ' justify-content-between' : ' justify-content-end'}`}>
        {title && (
          <h5 className="m-0">
            {title} {help && <Help placement="top">{help}</Help>}
          </h5>
        )}

        <div>
          {showRefresh && (
            <Button data-cy="refresh" variant="light" onClick={refresh}>
              {t('table.refresh')}
            </Button>
          )}

          {showClearFilters && (
            <Button data-cy="clear" className="ms-2" variant="light" onClick={clearFilters}>
              {' '}
              {t('table.clearFilters')}
            </Button>
          )}

          {showAddButton && onAddButtonClick && (
            <Button data-cy="add" className="ms-2" onClick={() => onAddButtonClick(true)}>
              {t('table.add')}
            </Button>
          )}
        </div>
      </div>

      <ReactTable hover borderless {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup: HeaderGroup) => {
            const { key: headerGroupKey, ...headerGroupProps } = headerGroup.getHeaderGroupProps();
            return (
              <tr key={headerGroupKey} {...headerGroupProps}>
                {headerGroup.headers.map((column) => {
                  const { key: headerKey, ...headerProps } = column.getHeaderProps();
                  return (
                    <th key={headerKey} {...headerProps}>
                      <div>
                        <span className="column-label" {...(column.canSort ? column.getSortByToggleProps() : {})}>
                          {column.render('Header')}
                          {column.isSorted && column.isSortedDesc && <FontAwesomeIcon icon="sort-down" aria-hidden="true" size="lg" />}
                          {column.isSorted && !column.isSortedDesc && <FontAwesomeIcon icon="sort-up" aria-hidden="true" size="lg" />}
                        </span>
                      </div>
                      <div>{column.canFilter ? column.render('Filter') : null}</div>
                    </th>
                  );
                })}
              </tr>
            );
          })}
        </thead>
        <tbody {...getTableBodyProps()}>
          {loading && (
            <>
              <tr className="loader">
                <td>
                  <Loader size="3x" beating />
                </td>
              </tr>
              <tr hidden />
              {/* Stay consistent with striped rows */}
            </>
          )}
          {!loading && pageCount === 0 && (
            <>
              <tr className="no-results">
                <td>{t('table.noDataText')}</td>
              </tr>
              <tr hidden />
              {/* Stay consistent with striped rows */}
            </>
          )}
          {createRowsFromPage({
            headerGroups,
            page: disablePagination ? rows : page,
            pageSize: disablePagination ? Math.max(totalElements, pageSize) : pageSize,
            prepareRow,
            onRowDoubleClick,
          })}
        </tbody>
      </ReactTable>
      <div className="pagination">
        <Pagination
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          previousPage={previousPage}
          nextPage={nextPage}
          gotoPage={gotoPage}
          pageCount={pageCount}
          pageIndex={pageIndex}
          setPageSize={setPageSize}
          pageSize={pageSize}
          pageSizeOptions={pageSizeOptions}
          totalElements={totalElements}
          showPagination={!disablePagination}
        />
      </div>
    </div>
  );
}

export const Table: React.FC<React.PropsWithChildren<TableProps>> = ({
  defaultSorters,
  defaultFilters,
  displayDefaultFilters,
  columns,
  mapFetchDataArgs,
  pageSize,
  disableFilters,
  showRefresh,
  showClearFilters,
  showAddButton,
  onFetchData,
  onRowDoubleClick,
  onAddButtonClick,
  afterFetchData,
  forceUpdate,
  forceData,
  title,
  disablePagination = false,
  help,
}) => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [skipPageReset, setSkipPageReset] = useState(false);

  const onFetchDataWithParams = useMemo(
    () =>
      ({ page, size, filters, sorters }: TableFetchDataType) => {
        const params = mapFetchDataArgs
          ? mapFetchDataArgs({ page, size, filters: mapFiltersToRequest(filters), sorters: mapSortToRequest(sorters) })
          : { page, size, filters: mapFiltersToRequest(filters), sorters: mapSortToRequest(sorters) };
        return onFetchData(params);
      },
    [mapFetchDataArgs, onFetchData]
  );

  const fetchData = useCallback(
    ({ page, size, filters, sorters }: TableFetchDataType) => {
      setLoading(true);

      setSkipPageReset(true);

      onFetchDataWithParams({ page, size, filters, sorters })
        .then(({ data }) => {
          setData(data.content);

          if (afterFetchData) {
            afterFetchData(data.content);
          }

          setPageCount(data.totalElements > 0 ? data.totalPages : 0);

          setTotalElements(data.totalElements);

          setSkipPageReset(false);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [afterFetchData, onFetchDataWithParams]
  );

  useEffect(() => {
    if (Array.isArray(forceData)) {
      setData([...forceData]);
    }
  }, [forceData]);

  return (
    <>
      <CustomTable
        className={disableFilters ? 'no-filters' : ''}
        columns={columns}
        data={data}
        fetchData={fetchData}
        loading={loading}
        pageCount={pageCount}
        pageSize={pageSize}
        disablePagination={disablePagination}
        defaultSorters={defaultSorters}
        defaultFilters={defaultFilters}
        displayDefaultFilters={displayDefaultFilters}
        skipPageReset={skipPageReset}
        onRowDoubleClick={onRowDoubleClick}
        disableFilters={!!disableFilters}
        totalElements={totalElements}
        showRefresh={showRefresh}
        showClearFilters={showClearFilters}
        showAddButton={showAddButton}
        onAddButtonClick={onAddButtonClick}
        forceUpdate={forceUpdate}
        title={title}
        help={help}
      />
    </>
  );
};
